<template>
  <a
    v-if="!card.archived"
    ref="childCardWrap"
    :href="cardLink"
    class="card-child-title-wrap no-underline"
    @mouseenter="displayPopover"
    @mouseleave="hidePopover"
    @click.prevent.stop="previewCard"
  >
    <em
      v-tooltip="{
        content: card.list_title,
        placement: 'top',
      }"
      class="chlid-card-status"
      :style="cardStatusStyle"
    />
    <span class="card-child-title">
      {{ card.title }}
    </span>
  </a>
</template>

<script>
import boardListCardMixin from '@/mixins/boardListCardMixin';
import cardPreviewPopoverMixin from '@/mixins/cardPreviewPopoverMixin';
import EventBus from '@/utilities/eventBus';
import cardStatuses from '@/mixins/cardStatuses';
import customIconsMixin from '@/mixins/customIconsMixin';
import getDashedTitle from '@/utilities/getDashedTitle';
import { ResourceType } from '@/types/resources';
import { getSprintKey } from '@/utilities/sprintKeys';
import { cmdClickOpenInNewTab } from '@/utilities/openInNewTab';
export default {
  name: 'ChildCardPreviewContent',

  mixins: [boardListCardMixin, cardPreviewPopoverMixin, customIconsMixin, cardStatuses],

  props: {
    cardId: {
      type: String,
      required: true,
    },

    parentCardId: {
      type: String,
      required: true,
    },

    // eslint-disable-next-line vue/no-unused-properties
    listCards: {
      // used in boardListCardMixin
      type: Array,
      required: false,
      default: () => [],
    },
  },

  computed: {
    cards() {
      return this.$store.getters.getCards;
    },

    card() {
      if (this.cards[this.cardId]) {
        return this.cards[this.cardId];
      }
      return this.cards[this.parentCardId].child_cards.find((c) => c.card_id === this.cardId);
    },

    flatBoards() {
      return this.$store.getters.getFlatBoards || [];
    },

    listId() {
      return !!this.card.sprint_id
        ? `${getSprintKey(this.card.project_id, this.card.sprint_id)}-${this.card.list_id}`
        : this.card.list_id;
    },

    childCardList() {
      return this.$store.getters.lists[this.listId] || {};
    },

    childCardStatus() {
      return (
        this.cardStatuses[this.card.status || this.childCardList.behavior] ||
        (Object.keys(this.childCardList).length ? this.childCardList : null) ||
        this.cardStatuses.backlog ||
        {}
      );
    },

    cardStatusIcon() {
      return this.childCardStatus.icon || '';
    },

    cardStatusStyle() {
      return {
        ...this.getCustomIconStyle(this.iconPath(this.cardStatusIcon || 'circle-dotted')),
        backgroundColor: this.card.list_color || this.childCardStatus.color || '#98A2B3',
      };
    },

    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },

    cardLink() {
      const dashedCardTitle = getDashedTitle(this.card.title);
      const cardRouteName = this.card.sprint_id ? ResourceType.SprintCard : ResourceType.Card;
      return `/${this.teamSlug}/${cardRouteName}-${this.cardId}-${dashedCardTitle}`;
    },
  },

  mounted() {
    const { cardId } = this;
    if (cardId && (!this.cards[cardId] || this.cards[cardId]?.partially_loaded)) {
      this.$store.dispatch('fetchCard', { cardId }).then((card) => {
        const sprintId = card.sprint_id;
        const isSprint = !!sprintId;

        const resourceKey = isSprint
          ? getSprintKey(card.project_id, card.sprint_id)
          : card.board_id;
        const flatBoard = this.flatBoards[resourceKey] || {};
        if (
          !Object.keys(this.childCardList).length &&
          !this.$isBoardFullyLoaded(flatBoard, isSprint)
        ) {
          if (isSprint) {
            this.$store.dispatch('getSprint', {
              projectId: card.project_id,
              sprintId,
            });
            return;
          }
          this.$store.dispatch('fetchBoard', { boardId: card.board_id });
        }
      });
    }
  },

  methods: {
    previewCard(event) {
      this.hidePopover();
      if (cmdClickOpenInNewTab(this.cardLink, event)) return;
      this.setCurrentCard();
      this.$store.dispatch('setPreviewPopoverKey', false);
      this.$router.push({
        path: this.cardLink,
        query: { userNavigated: true },
      });
    },

    displayPopover() {
      const { childCardWrap } = this.$refs;
      const rect = childCardWrap?.getBoundingClientRect() || {};
      EventBus.$emit('child-quick-preview', this.cardId, rect);
      if (this.draggingIsOn || this.popOverEnabled) return;
      this.popOverEnabled = true;
    },

    hidePopover() {
      EventBus.$emit('child-quick-preview', '');
      if (!this.popOverEnabled) return;
      this.popOverEnabled = false;
    },
  },
};
</script>

<style lang="scss">
$iconDimension: 16px;

.card-child-title-wrap {
  display: flex;
  align-items: center;
  height: 32px;
  position: relative;
  padding: 0 15px;

  &:hover {
    @apply bg-hover-onHover;
  }

  &::before {
    content: '';
    display: block;
    width: calc(100% - (2 * 15px));
    height: 1px;
    position: absolute;
    top: 0;
    @apply bg-dividers-default;
  }

  .chlid-card-status {
    width: $iconDimension;
    height: $iconDimension;
    min-width: $iconDimension;
    min-height: $iconDimension;
  }

  .card-child-title {
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.35px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-left: 8px;
    @apply text-text-default;
  }
}
</style>
